import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Sparkles, Trophy, LineChart, Crown, Swords, Gem } from "lucide-react";

const SLIDES = [
  {
    icon: Sparkles,
    color: "#00F0FF",
    title: "Welcome to Fanpuff",
    body: "A free-to-play fantasy game where you back the stars you love. Every star scores Form Points (0–100) — higher Form = more fan buzz this week.",
  },
  {
    icon: LineChart,
    color: "#00FF66",
    title: "Browse Top Form",
    body: "237+ stars across Bollywood, reality TV, creators, music & athletes. Tap any card to see their Form Points and a Dream11-style breakdown of how they're scoring right now.",
  },
  {
    icon: Trophy,
    color: "#FFD600",
    title: "Build a 6-star Influencer Circle",
    body: "Pick your favourite contest tier (Mini Daily 2k → Season Cup 50k credits), choose 6 stars, and lock your Circle. Top finishers split a 70% prize pool.",
  },
  {
    icon: Crown,
    color: "#B026FF",
    title: "Crown a Captain (2×) + Vice (1.5×)",
    body: "Your Captain doubles their points, Vice-Captain gets a 1.5× boost. Tip: pick someone with a fresh release, big news, or surging buzz.",
  },
  {
    icon: Swords,
    color: "#FF0055",
    title: "Duel a friend 1v1",
    body: "Tap 'Duel a friend', build a 6-pick Circle, share the link on WhatsApp / Insta. Whoever scores higher wins instantly. Bragging rights forever.",
  },
  {
    icon: Gem,
    color: "#FFD600",
    title: "Win Credits → Unlock Real Rewards",
    body: "Grow your Fanpuff Credits wallet. 150k → BookMyShow voucher · 500k → boAt earbuds · 1.8M → smartwatch · 8M → iPhone 17 · 12M → Smart TV / MacBook. Free. No cash. Ever.",
  },
];

export default function QuickTour({ open, onClose, storageKey = "fanpuff_tour_seen" }) {
  const [i, setI] = useState(0);

  const close = () => {
    try { localStorage.setItem(storageKey, "1"); } catch {}
    onClose && onClose();
    setI(0);
  };

  if (!open) return null;
  const slide = SLIDES[i];
  const Icon = slide.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center p-4"
        data-testid="quick-tour"
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="relative w-full max-w-md glass-strong rounded-3xl p-6 border border-white/10"
        >
          <button
            onClick={close}
            data-testid="tour-close"
            className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full hover:bg-white/10"
            aria-label="close"
          >
            <X className="w-4 h-4" />
          </button>

          <div
            className="w-14 h-14 rounded-2xl grid place-items-center"
            style={{ background: `${slide.color}22`, border: `1px solid ${slide.color}55` }}
          >
            <Icon className="w-6 h-6" style={{ color: slide.color }} />
          </div>
          <p className="mt-4 text-xs tracking-widest uppercase text-white/50 font-bold">Step {i + 1} of {SLIDES.length}</p>
          <h3 className="font-display text-2xl font-bold mt-1">{slide.title}</h3>
          <p className="text-sm text-white/70 mt-2 leading-relaxed">{slide.body}</p>

          <div className="flex items-center justify-between gap-2 mt-6">
            <div className="flex gap-1">
              {SLIDES.map((_, idx) => (
                <span
                  key={idx}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: idx === i ? 22 : 8,
                    background: idx === i ? "linear-gradient(90deg,#B026FF,#00F0FF)" : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {i > 0 && (
                <button
                  onClick={() => setI(i - 1)}
                  className="px-4 py-2 rounded-full glass hover:bg-white/10 text-sm"
                  data-testid="tour-prev"
                >Back</button>
              )}
              {i < SLIDES.length - 1 ? (
                <button
                  onClick={() => setI(i + 1)}
                  data-testid="tour-next"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-black font-bold text-sm"
                  style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={close}
                  data-testid="tour-finish"
                  className="px-4 py-2 rounded-full text-black font-bold text-sm"
                  style={{ background: "linear-gradient(90deg,#00FF66,#00F0FF)" }}
                >
                  Let's play
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
